import React from 'react';
import {Box, Card, Chip, Tooltip} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {EditRounded, LinkRounded, PersonRemoveRounded, SubdirectoryArrowRightRounded} from "@mui-symbols-material/w300";
import {ActionsMenu} from "@dreebit-registry/vsm-microservice-components";
import {ActionItemConfig} from "@dreebit-registry/vsm-microservice-components/dist/components/ActionsMenu";
import {ContactAssignment, ContactPersonInfo} from "../../utils/contactAssignments";
import ContactChannelLinks from "../ContactChannelLinks";
import ContactNameRow from "../ContactNameRow";

interface Props {
    assignment: ContactAssignment;
    canWrite: boolean;
    onEdit: (person: ContactPersonInfo) => void;
    onRemove: (person: ContactPersonInfo) => void;
    onNavigateToOrigin: (inheritedFrom: NonNullable<ContactAssignment['inheritedFrom']>) => void;
}

/**
 * One contact person on the node contact tab. A directly assigned contact gets
 * the kebab actions (edit person, remove from this node); an inherited one is
 * read-only and shows where its assignment sits — the chip navigates the tree
 * to that ancestor. Deleting a person altogether is not offered here — that
 * belongs to the company's contact list in the CRM.
 *
 * Cards keep uniform dimensions: the name row and the contact block reserve
 * their height whether or not a role / email / phone are present.
 */
const ContactPersonCard = ({assignment, canWrite, onEdit, onRemove, onNavigateToOrigin}: Props) => {
    const {t} = useTranslation();
    const person = assignment.contactPerson;
    const inheritedFrom = assignment.inheritedFrom;

    // sourceKind != OWN: name/email/phone resolve live through an OTTO user or
    // a service-provider contact. The edit form shows them read-only; the role
    // stays editable, so editing is offered as usual.
    const isLinked = !!person.sourceKind && person.sourceKind !== 'OWN';
    const linkedSourceText = person.sourceKind === 'SERVICE_PROVIDER_CONTACT'
        ? t('Provided by your service provider.')
        : t("Kept up to date from this person's OTTO account.");

    const menuItems: ActionItemConfig[] = [
        {
            item: (
                <Box display="flex" alignItems="center">
                    <EditRounded fontSize="inherit" sx={{mr: '4px'}}/>
                    {t('Edit contact')}
                </Box>
            ),
            onClick: () => onEdit(person),
            active: true,
        },
        {
            item: (
                <Box display="flex" alignItems="center">
                    <PersonRemoveRounded fontSize="inherit" sx={{mr: '4px'}}/>
                    {t('Remove from this element')}
                </Box>
            ),
            onClick: () => onRemove(person),
            active: true,
        },
    ];

    return (
        <Card
            variant="outlined"
            data-testid="contact-card"
            sx={{
                height: '100%',
                backgroundColor: '#fff',
                gap: '6px',
                p: '12px 14px',
            }}
        >
            <Box sx={{display: 'flex', gap: 1}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <ContactNameRow name={person.name} role={person.role} roleTestId="contact-role-chip">
                        {isLinked && (
                            <Tooltip title={linkedSourceText} variant="soft" placement="top">
                                <Chip
                                    size="sm"
                                    color="neutral"
                                    variant="soft"
                                    data-testid="contact-linked-chip"
                                    startDecorator={<LinkRounded sx={{fontSize: 14}}/>}
                                >
                                    {t('Linked')}
                                </Chip>
                            </Tooltip>
                        )}
                    </ContactNameRow>
                </Box>
                {!inheritedFrom && canWrite && (
                    // name-row height: the taller menu button overhangs instead of
                    // pushing the channel links down (same spacing as the
                    // service center's named contacts)
                    <Box sx={{flexShrink: 0, height: '24px', display: 'flex', alignItems: 'center'}}>
                        <ActionsMenu items={menuItems}/>
                    </Box>
                )}
            </Box>

            {/* Fixed two-line block so cards match whether or not email/phone exist. */}
            <ContactChannelLinks email={person.email} phone={person.phone} sx={{height: '42px', overflow: 'hidden'}}/>

            {inheritedFrom && (
                <Box>
                    <Chip
                        size="sm"
                        color="neutral"
                        variant="outlined"
                        data-testid="contact-inherited-chip"
                        startDecorator={<SubdirectoryArrowRightRounded sx={{fontSize: 14}}/>}
                        onClick={() => onNavigateToOrigin(inheritedFrom)}
                        sx={{cursor: 'pointer'}}
                    >
                        {t('Inherited from "{{title}}"', {title: inheritedFrom.title ?? ''})}
                    </Chip>
                </Box>
            )}
        </Card>
    );
};

export default ContactPersonCard;
